import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, useMediaQuery } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useAuthState } from "../app/AuthStateContext";
import { MOBILE_NAV_MEDIA } from "../layout/layoutConstants";
import AccountSettingsSidebar from "./AccountSettingsSidebar";
import AccountTabSection from "./AccountTabSection";
import MyReportsPage from "./MyReportsPage";
import NotificationsPage from "./NotificationsPage";
import PublicProfilePage from "./PublicProfilePage";
import ReferralProgramPage from "./ReferralProgramPage";
import SubscriptionPage from "./SubscriptionPage";
import SupportTicketsPage from "./SupportTicketsPage";
import TwoFactorAuthPage from "./TwoFactorAuthPage";
import {
    getAccountSettingsSectionFromLocation,
    replaceSettingsSectionUrl,
    resolveAccountSettingsSection,
} from "./accountSettingsNav";
import {
    ACCOUNT_PAGE_CARD_NOISE_SX,
    ACCOUNT_PAGE_MAIN_SX,
    ACCOUNT_PAGE_MOBILE_EMBEDDED_MAIN_SX,
    ACCOUNT_PAGE_MOBILE_UNIFIED_DIVIDER_SX,
    ACCOUNT_PAGE_MOBILE_UNIFIED_SX,
    ACCOUNT_PAGE_SHELL_SX,
    ACCOUNT_PAGE_SIDEBAR_STICKY_SX,
    ACCOUNT_PAGE_SPLIT_SX,
} from "./accountPageStyles";

const SettingsMainPane = React.memo(function SettingsMainPane({
    activeSection,
    user,
    membership,
    avatarCacheBust,
    actions,
    refreshToken,
    onSectionChange,
    mobileEmbedded = false,
}) {
    const content =
        activeSection === "account" ? (
            <AccountTabSection
                user={user}
                actions={actions}
                refreshToken={refreshToken}
                onSectionChange={onSectionChange}
            />
        ) : activeSection === "public-profile" ? (
            <PublicProfilePage
                user={user}
                actions={actions}
                avatarCacheBust={avatarCacheBust}
            />
        ) : activeSection === "two-factor-auth" ? (
            <TwoFactorAuthPage user={user} />
        ) : activeSection === "notifications" ? (
            <NotificationsPage user={user} actions={actions} />
        ) : activeSection === "subscriptions" ? (
            <SubscriptionPage
                user={user}
                membership={membership}
                actions={actions}
                onSectionChange={onSectionChange}
            />
        ) : activeSection === "referral-program" ? (
            <ReferralProgramPage membership={membership} />
        ) : activeSection === "my-reports" ? (
            <MyReportsPage />
        ) : activeSection === "support-tickets" ? (
            <SupportTicketsPage user={user} avatarCacheBust={avatarCacheBust} />
        ) : null;

    if (mobileEmbedded) {
        return <Box sx={ACCOUNT_PAGE_MOBILE_EMBEDDED_MAIN_SX}>{content}</Box>;
    }

    return (
        <Box sx={ACCOUNT_PAGE_MAIN_SX}>
            <Box sx={ACCOUNT_PAGE_CARD_NOISE_SX} />
            {content}
        </Box>
    );
});

SettingsMainPane.propTypes = {
    activeSection: PropTypes.string,
    user: PropTypes.object,
    membership: PropTypes.object,
    avatarCacheBust: PropTypes.number,
    actions: PropTypes.object,
    refreshToken: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
        PropTypes.object,
    ]),
    onSectionChange: PropTypes.func,
    mobileEmbedded: PropTypes.bool,
};

export default function AccountPage({ catalog = {}, actions }) {
    const location = useLocation();
    const { logout } = useAuthState();
    const isMobile = useMediaQuery(MOBILE_NAV_MEDIA);
    const [activeSection, setActiveSection] = useState(() =>
        getAccountSettingsSectionFromLocation(location),
    );

    useEffect(() => {
        replaceSettingsSectionUrl(activeSection);
    }, [activeSection]);

    const handleSectionChange = useCallback((sectionId) => {
        setActiveSection((current) => {
            const next = resolveAccountSettingsSection(sectionId);
            return next === current ? current : next;
        });
    }, []);

    const user = catalog?.user;

    useEffect(() => {
        if (user?.userId) {
            actions?.fetchMembershipStatus?.(user.userId);
        }
    }, [actions, user?.userId]);

    const { currentLobby, currentLobbyCode, matchmakingStarted } = catalog;
    const isLeader = user && currentLobby ? currentLobby.hostId === user.userId : false;
    const logoutBlocked =
        catalog?.lobbyState === "searching" ||
        (matchmakingStarted && !isLeader && currentLobbyCode);

    const handleLogoutBlocked = useCallback(() => {
        actions?.showUnavailableActionMessage?.();
    }, [actions]);

    return (
        <Box sx={ACCOUNT_PAGE_SHELL_SX}>
            {isMobile ? (
                <Box sx={ACCOUNT_PAGE_MOBILE_UNIFIED_SX}>
                    <Box sx={ACCOUNT_PAGE_CARD_NOISE_SX} />
                    <AccountSettingsSidebar
                        mobileEmbedded
                        activeSection={activeSection}
                        onSectionChange={handleSectionChange}
                        user={catalog?.user}
                        membership={catalog?.membership}
                        avatarCacheBust={catalog?.avatarCacheBust}
                        logout={logout}
                        logoutBlocked={logoutBlocked}
                        onLogoutBlocked={handleLogoutBlocked}
                    />
                    <Box aria-hidden="true" sx={ACCOUNT_PAGE_MOBILE_UNIFIED_DIVIDER_SX} />
                    <SettingsMainPane
                        mobileEmbedded
                        activeSection={activeSection}
                        user={user}
                        membership={catalog?.membership}
                        avatarCacheBust={catalog?.avatarCacheBust}
                        actions={actions}
                        refreshToken={catalog?.accountUpdateCompleteResult}
                        onSectionChange={handleSectionChange}
                    />
                </Box>
            ) : (
                <Box sx={ACCOUNT_PAGE_SPLIT_SX}>
                    <Box sx={ACCOUNT_PAGE_SIDEBAR_STICKY_SX}>
                        <AccountSettingsSidebar
                            activeSection={activeSection}
                            onSectionChange={handleSectionChange}
                            user={catalog?.user}
                            membership={catalog?.membership}
                            avatarCacheBust={catalog?.avatarCacheBust}
                            logout={logout}
                            logoutBlocked={logoutBlocked}
                            onLogoutBlocked={handleLogoutBlocked}
                        />
                    </Box>
                    <SettingsMainPane
                        activeSection={activeSection}
                        user={user}
                        membership={catalog?.membership}
                        avatarCacheBust={catalog?.avatarCacheBust}
                        actions={actions}
                        refreshToken={catalog?.accountUpdateCompleteResult}
                        onSectionChange={handleSectionChange}
                    />
                </Box>
            )}
        </Box>
    );
}

AccountPage.propTypes = {
    actions: PropTypes.object,
    catalog: PropTypes.object,
};
